import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Custom hook for managing table state in URL query parameters.
 * Provides persistent, shareable, bookmarkable table state.
 *
 * @param {Object} options - Configuration options
 * @param {number} options.defaultSize - Default page size (default: 25)
 * @param {string} options.defaultSortKey - Default sort column (default: '')
 * @param {string} options.defaultSortDirection - Default sort direction (default: '')
 * @returns {Object} Table state and setters
 */
const useTableState = (options = {}) => {
  const {
    defaultSize = 25,
    defaultSortKey = '',
    defaultSortDirection = ''
  } = options;

  const [searchParams, setSearchParams] = useSearchParams();

  // Parse current values from URL
  const page = parseInt(searchParams.get('page') || '0', 10);
  const size = parseInt(searchParams.get('size') || String(defaultSize), 10);
  const search = searchParams.get('search') || '';
  const sortKey = searchParams.get('sortKey') || defaultSortKey;
  const sortDirection = searchParams.get('sortDirection') || defaultSortDirection;

  // Parse filters from URL (filter[key]=value format)
  const filters = useMemo(() => {
    const result = {};
    for (const [key, value] of searchParams.entries()) {
      if (key.startsWith('filter[') && key.endsWith(']')) {
        const filterKey = key.slice(7, -1); // Extract key from filter[key]

        // Check if it's a comma-separated list (for checkbox filters)
        if (value.includes(',')) {
          result[filterKey] = value.split(',');
        } else {
          result[filterKey] = value;
        }
      }
    }
    return result;
  }, [searchParams]);

  // Helper to update params while preserving others
  const updateParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Setters
  const setPage = useCallback((newPage) => {
    updateParams({ page: newPage === 0 ? null : newPage });
  }, [updateParams]);

  const setSize = useCallback((newSize) => {
    updateParams({
      size: newSize === defaultSize ? null : newSize,
      page: null // Reset to first page when size changes
    });
  }, [updateParams, defaultSize]);

  const setSearch = useCallback((newSearch) => {
    updateParams({
      search: newSearch || null,
      page: null // Reset to first page when search changes
    });
  }, [updateParams]);

  const setSortKey = useCallback((newSortKey) => {
    updateParams({ sortKey: newSortKey || null });
  }, [updateParams]);

  const setSortDirection = useCallback((newSortDirection) => {
    updateParams({ sortDirection: newSortDirection || null });
  }, [updateParams]);

  const setFilters = useCallback((newFilters) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      // Remove all existing filter params
      const keysToDelete = [];
      for (const key of newParams.keys()) {
        if (key.startsWith('filter[')) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => newParams.delete(key));

      // Add new filter params
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '' ||
            (Array.isArray(value) && value.length === 0)) {
          return; // Skip empty values
        }

        const paramValue = Array.isArray(value) ? value.join(',') : String(value);
        newParams.set(`filter[${key}]`, paramValue);
      });

      // Reset to first page when filters change
      newParams.delete('page');

      return newParams;
    });
  }, [setSearchParams]);

  // Convenience method to update a single filter
  const setFilter = useCallback((key, value) => {
    setFilters({ ...filters, [key]: value });
  }, [filters, setFilters]);

  // Build query string for API calls (matches existing format)
  const buildApiQuery = useCallback(() => {
    let query = `?page=${page}&size=${size}`;

    if (search) query += `&search=${encodeURIComponent(search)}`;
    if (sortDirection) query += `&sortDirection=${sortDirection}`;
    if (sortKey) query += `&sortKey=${sortKey}`;

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        query += `&filter[${key}]=${value.join(',')}`;
      } else if (value) {
        query += `&filter[${key}]=${encodeURIComponent(value)}`;
      }
    });

    return query;
  }, [page, size, search, sortKey, sortDirection, filters]);

  // Reset all state
  const resetState = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return {
    // State values
    page,
    size,
    search,
    sortKey,
    sortDirection,
    filters,

    // Setters
    setPage,
    setSize,
    setSearch,
    setSortKey,
    setSortDirection,
    setFilters,
    setFilter,

    // Utilities
    buildApiQuery,
    resetState,

    // For components that need direct access
    searchParams,
    setSearchParams
  };
};

export default useTableState;
